import { Fragment, useRef, useLayoutEffect } from 'react'
import cn from 'classnames'
import { string, shape, number, instanceOf } from 'prop-types'
import { CSSTransition } from 'react-transition-group'

import Video from '../../theme-components/video.js'
import PostTypeIndicator from '../../theme-components/PostTypeIndicator.js'
import GridFeedPostMinimalisticOverlay from './grid-feed-post-minimalistic-overlay.js'
import GridFeedPostOverlay from './grid-feed-post-overlay.js'
import Icon from '../../../icon/index.js'
import keyPress from '../../../../utilities/keyPress.js'
import GateContext from '../../../../utilities/gateContext.js'
import PostImage from '../../theme-components/post-image.js'
import Settings from '../../../../../models/settings.js'
import Post from '../../../../../models/Post.js'
import Locale from '../../../../../models/Locale.js'
import preloadPostMedia from '../../../../utilities/preloadPostMedia.js'

const GridFeedPost = ({
  additionalStyles = {},
  observer = null,
  post,
  settings,
  width,
  feedKey,
  transitionOptions = null,
  locale,
}) => {
  const mainRef = useRef()
  useLayoutEffect(() => {
    if (!observer) {
      return () => {}
    }

    const { current } = mainRef
    observer.observe(current)
    return () => {
      observer.unobserve(current)
    }
  }, [observer])

  const { postInteractivity, showPostText, gutterSize, autoplayVideos, isMinimalistic } = settings
  let Wrapper = Fragment
  let wrapperProps = {}
  if (transitionOptions) {
    Wrapper = CSSTransition
    wrapperProps = {
      nodeRef: mainRef,
      ...transitionOptions,
    }
  }
  return (
    <GateContext.Consumer>
      {({ openGate }) => (
        <Wrapper {...wrapperProps}>
          <div
            ref={mainRef}
            className="bzfy-c-tilegrid-post-container bzfy-c-tilegrid-post-container--clickable"
            data-post-key={post.key}
            onClick={() => openGate(post)}
            onKeyDown={(e) => {
              keyPress(e, () => openGate(post))
            }}
            onMouseEnter={() => preloadPostMedia(post)}
            role="link"
            style={{ padding: gutterSize / 2, ...additionalStyles }}
            tabIndex={0}
            title={locale.getTranslation('openTheGate')}
          >
            <div className="bzfy-c-tilegrid-post-container__inner">
              <div
                className={cn('bzfy-c-tilegrid-post', {
                  'bzfy-c-tilegrid-post--static': !postInteractivity || !showPostText,
                })}
              >
                <div className="bzfy-c-tilegrid-post__background">
                  {post.getIsVideoPost() && autoplayVideos ? (
                    <Video
                      autoPlay={autoplayVideos}
                      mediaIndex={post.thumbnailMediaPosition}
                      post={post}
                      width={width}
                    />
                  ) : (
                    <PostImage locale={settings.locale} post={post} sizes={width} />
                  )}
                  {isMinimalistic ? (
                    <GridFeedPostMinimalisticOverlay
                      feedKey={feedKey}
                      post={post}
                      settings={settings}
                    />
                  ) : (
                    <GridFeedPostOverlay feedKey={feedKey} post={post} settings={settings} />
                  )}
                  <PostTypeIndicator
                    autoplayEnabled={autoplayVideos}
                    post={post}
                    settings={settings}
                  />
                </div>
                <span aria-hidden="true" className="bzfy-o-likes">
                  {post.likes}
                  <Icon color="#000" icon="likes" />
                </span>
              </div>
            </div>
          </div>
        </Wrapper>
      )}
    </GateContext.Consumer>
  )
}

GridFeedPost.propTypes = {
  additionalStyles: shape({}),
  feedKey: string.isRequired,
  locale: instanceOf(Locale).isRequired,
  observer: instanceOf(IntersectionObserver),
  post: instanceOf(Post).isRequired,
  settings: instanceOf(Settings).isRequired,
  transitionOptions: shape({}),
  width: number.isRequired,
}

export default GridFeedPost
